import { createEditCalendarEvent, generateZugferdInvoice, regeneratePDF } from "@rpforms/shared";
import {
    getFolderLocation,
    updateFormEntry,
    updateFormEntryStatus,
} from "@rpforms/shared/build/actions";
import * as actions from "@rpforms/shared/build/actions/formEntries";
import {
    fetchFormEntryPdfUrl,
    fetchUploadDownloadUrl,
    openSignedUrl,
} from "@rpforms/shared/build/actions/signedDownloads";
import {
    SmallButton,
    SmallPrimaryButton,
    SmallDangerButton,
} from "@rpforms/shared/build/components/layout/Button";
import { RightAligned } from "@rpforms/shared/build/components/universal/PageNavbar";
import { push } from "connected-react-router";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import { connect, useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { toast } from "react-toastify";
import { useSearchParam } from "react-use";
import styled from "styled-components";
import { ListRow } from "../forms/FormListRow";
import { DraggableFile } from "../universal/DraggableFile";
import EntryRowOptions from "./EntryRowOptions";
import EntryRowState from "./EntryRowState";
import { FormName } from "./FormEntriesRow";

const LoadingOverlay = styled.div`
    background: rgba(0, 0, 0, 0.3);
    position: absolute;
    inset: 0;
    z-index: 20;
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-weight: bold;
`;

export type FormEntryState =
    | "open"
    | "processed"
    | "accepted"
    | "paid"
    | "rejected"
    | "reminded"
    | "voucher"
    | "canceled"
    | "offer_needed";

const ZUGFERD_IMPLEMENTED_AT = "2025-05-27";

const EntryRow = ({ entry, deleteFormEntry, isSelected, onSelect }) => {
    const previousPage = parseInt(useSearchParam("page"), 10) || 1;
    const [renaming, setRenaming] = useState(false);
    const [show, setShow] = useState(false);
    const { calendarEvents, isLoading } = useSelector<any, any>((state) => state.calendarEvents);
    const [editEntryId, setEntryEditId] = useState(null);
    const { regeneratingPDF } = useSelector<any, any>((state) => state.formEntries);

    const dispatch = useDispatch();
    const renamingField = useRef<any>(null);
    const entryDate = moment(new Date(entry.created_at)).format("DD.MM.YYYY HH:mm");
    const stateUpdatedAt = entry.human_state_updated_at || "";
    const ipadName = entry.device_name;
    const handleClose = () => {
        setShow(false);
    };

    useEffect(() => {
        if (isLoading || editEntryId !== entry.id) {
            return;
        }
        setEntryEditId(null);
        if (calendarEvents.length === 0) {
            toast.error(
                "Dem Formular konnte kein Gerät zugeordnet werden, " +
                    "bitte erstellen Sie die Nachbearbeitung manuell im Kalender"
            );
            return;
        }
        toast.success("Nachbearbeitungsauftrag wurde erfolgreich erstellt");
    }, [isLoading]);

    const deleteEntry = () => {
        if (confirm("Sind Sie sicher?")) {
            deleteFormEntry(entry);
        }
    };

    // creates a new deviceTask of type 'Nachbearbeitung'
    const buildObjectLabel = (formEntry) => {
        const entity = formEntry?.entity;
        const entityName = entity?.name || "-";
        const address = entity?.address || {};
        const addressParts = [
            address.street,
            [address.postalcode, address.city].filter(Boolean).join(" "),
        ].filter(Boolean);
        const addressLabel = addressParts.length > 0 ? ` - ${addressParts.join(", ")}` : "";

        return `Objekt: ${entityName}${addressLabel}`;
    };

    const createEditTaskClick = (entryId: number, formEntry?: any) => {
        const protocolType = formEntry?.form?.name || "";
        const objectLabel = buildObjectLabel(formEntry);
        dispatch(
            createEditCalendarEvent(entryId, false, {
                protocol_type: protocolType,
                object_label: objectLabel,
            } as any)
        );
        setEntryEditId(entryId);
    };

    const renameEntry = () => {
        setRenaming(!renaming);
    };

    const saveEntry = async () => {
        const nextName = renamingField?.current ? renamingField.current.value : "";
        if (!nextName) {
            return;
        }
        setRenaming(false);
        await dispatch(
            updateFormEntry({
                ...entry,
                name: nextName,
            })
        );
    };

    const updateState = async (id, state, terms) => {
        await dispatch(updateFormEntryStatus(id, state, terms));
    };

    const openInFolder = async () => {
        const req = await dispatch(
            getFolderLocation({
                resource_id: entry.id,
                resource_type: "FormEntry",
            })
        );
        dispatch(push("/folders?open=" + encodeURIComponent(JSON.stringify(req.data))));
    };

    const openEntryInFolder = async (entry) => {
        const req = await dispatch(
            getFolderLocation({
                resource_id: entry,
                resource_type: "FormEntry",
            })
        );
        dispatch(push("/folders?open=" + encodeURIComponent(JSON.stringify(req.data))));
    };

    const migrate = async () => {
        // dispatch(push("/entries/editor/migrate/source/" + entry.id + "/target/" + entry.form.id));
        window.location.assign(
            "/entries/editor/migrate/source/" + entry.id + "/target/" + entry.form.id
        );
    };

    const template = async () => {
        dispatch(push("/entries/editor/template/source/" + entry.id + "/target/" + entry.form.id));
    };
    const regenerate = async () => {
        dispatch(regeneratePDF(entry.id));
    };

    const createZugferdInvoice = async () => {
        dispatch(generateZugferdInvoice(entry.id));
    };

    const pdf = entry.pdf;
    const isLoadingPdf = regeneratingPDF === entry.id;

    const isInvoice = entry?.form?.name === "000_Rechnung";

    const pdfDownloadName = `${entry.name || `Dokument-${entry.id}`}.pdf`;

    const pdfButton = () => {
        if (pdf) {
            return (
                <DraggableFile
                    getUrl={() => fetchFormEntryPdfUrl(entry.id)}
                    filename={pdfDownloadName}
                    mime="application/pdf"
                >
                    <SmallButton
                        style={{ cursor: "pointer" }}
                        onClick={() => openSignedUrl(fetchFormEntryPdfUrl(entry.id))}
                    >
                        <i className="fa fa-file-pdf-o"></i>{" "}
                        {isInvoice && entry.has_xml ? "E-Rechnung" : "PDF"}
                    </SmallButton>
                </DraggableFile>
            );
        }
        if (
            (!isInvoice && !pdf) ||
            (isInvoice &&
                moment(entry.updated_at).isAfter(moment(ZUGFERD_IMPLEMENTED_AT)) &&
                (!pdf || !entry.has_xml))
        ) {
            return (
                <SmallButton disabled>
                    <i className="fa fa-file-pdf-o"></i>{" "}
                    {isInvoice ? "Erstelle E-Rechnung..." : "Erstelle PDF..."}
                </SmallButton>
            );
        }
        if (!isInvoice && !pdf) {
            return (
                <a href={`${entry.pdf}`} target={"_blank"}>
                    <SmallButton>
                        <i className="fa fa-file-pdf-o"></i> PDF
                    </SmallButton>
                </a>
            );
        }
        return null;
    };

    return (
        <>
            <ListRow data-id={entry.id}>
                {onSelect && (
                    <div style={{ marginRight: "10px", display: "flex", alignItems: "center" }}>
                        <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => onSelect(e.target.checked)}
                        />
                    </div>
                )}
                <div>
                    <FormName>
                        {!renaming && entry.name}
                        {renaming && (
                            <>
                                <input
                                    type="text"
                                    ref={renamingField}
                                    className="form-control"
                                    defaultValue={entry.name}
                                />
                                <SmallButton onClick={saveEntry}>
                                    <i className={"fa fa-check"}></i>
                                </SmallButton>
                                <SmallButton onClick={renameEntry}>
                                    <i className={"fa fa-close"}></i>
                                </SmallButton>
                            </>
                        )}
                    </FormName>
                    <small>
                        {entry.terms && entry.terms.length > 0 && (
                            <>
                                {entry.terms.map((term) => {
                                    return (
                                        <Badge
                                            key={`${entry.id}_${term.id}`}
                                            className="mx-1"
                                            style={{
                                                backgroundColor:
                                                    term.name === "Wartungsvertrag"
                                                        ? "#777"
                                                        : term.options.backgroundColor,
                                                color: term.options.color,
                                            }}
                                        >
                                            {term.name}
                                        </Badge>
                                    );
                                })}
                                &nbsp;
                            </>
                        )}
                        {entry.entity && (
                            <>
                                Objekt: <strong>{entry.entity.name}</strong>|{" "}
                            </>
                        )}
                        Formular: <strong>{entry.form.name}</strong>| Info:{" "}
                        <strong>{entryDate}</strong> | iPad: <strong>{ipadName}</strong>
                        {entry.meta && entry.meta.summary && (
                            <>
                                <br />
                                {entry.meta.summary}
                            </>
                        )}
                    </small>
                </div>

                {isLoadingPdf && (
                    <LoadingOverlay>
                        <Spinner animation={"border"} size={"sm"} /> <span>&nbsp;</span>
                        {isInvoice ? "E-Rechnung wird generiert" : "PDF wird generiert"}
                    </LoadingOverlay>
                )}

                <RightAligned>
                    {!entry.invoice && (
                        <EntryRowOptions
                            {...{
                                entry,
                                previousPage,
                                migrate,
                                regenerate,
                                template,
                                setShow,
                                deleteEntry,
                                createEditTaskClick,
                            }}
                        />
                    )}
                    {pdfButton()}

                    <SmallPrimaryButton onClick={openInFolder}>
                        <i className="fa fa-folder-open" /> Ordnerstruktur
                    </SmallPrimaryButton>
                    {entry.invoice && (
                        <SmallDangerButton onClick={() => openEntryInFolder(entry.invoice_id)}>
                            <i className="fa fa-file" /> Zur Rechnung
                        </SmallDangerButton>
                    )}
                    {!entry.invoice && (
                        <EntryRowState
                            stateUpdatedAt={stateUpdatedAt}
                            state={entry.state}
                            humanState={entry.human_state}
                            updateState={updateState}
                            formGroupName={entry.form?.form_group?.name}
                            id={entry.id}
                            entry={entry}
                            possibleEvents={entry?.possible_events || []}
                        />
                    )}
                </RightAligned>
            </ListRow>
            {show && (
                <Modal
                    size="lg"
                    show={show}
                    onHide={handleClose}
                    backdrop="static"
                    keyboard={false}
                >
                    <Modal.Body>
                        {entry.uploads.map((upload) => {
                            return (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <div>{upload.name || upload.unique_id} </div>
                                    <div>
                                        <a
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                                openSignedUrl(fetchUploadDownloadUrl(upload.id))
                                            }
                                        >
                                            <SmallButton>Ansehen</SmallButton>
                                        </a>
                                    </div>
                                </div>
                            );
                        })}
                        {entry.attachments.map((attachment) => {
                            if (!attachment || !attachment.id) {
                                return <div></div>;
                            }
                            return (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <div>
                                        {attachment.name}{" "}
                                        <small
                                            style={{
                                                color: "#aaa",
                                                fontSize: "10px",
                                            }}
                                        >
                                            [{attachment.id && attachment.id.substr(11, 8)}]
                                        </small>
                                    </div>
                                    <div>
                                        <a
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                                openSignedUrl(fetchUploadDownloadUrl(attachment.id))
                                            }
                                        >
                                            <SmallButton>Ansehen</SmallButton>
                                        </a>
                                    </div>
                                </div>
                            );
                        })}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Schließen
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

function mapStateToProps(state) {
    return {};
}

const ConnectedEntryRow = connect(mapStateToProps, actions)(EntryRow as any);

export default withRouter(ConnectedEntryRow as any);
